import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Container,
  Box,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import { MoreVert } from "@mui/icons-material";
import { Search, X } from "lucide-react";
import {
  PHARMACY_IPD_LIST,
  PHARMACY_OPD_LIST,
  PHARMACY_OUTSIDER_LIST,
} from "../_mock/mock";
import { useGetApi } from "../hooks/useGetApi";
import { getPharmacyRequestApi } from "../services/medicine-tracking.service";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";

// Pharmacy Status Button Component - Production Safe
const PharmacyStatusButton = ({ label, isActive, onClick, width = "110px", count }) => {
  const theme = useTheme();
  
  // Force production-safe rendering with explicit styles
  const buttonStyles = {
    border: `2px solid ${isActive ? theme.palette.primary.dark : "#92A6CC"}`,
    color: isActive ? theme.palette.primary.dark : "text.secondary",
    backgroundColor: isActive ? "#AED5F3" : "#F0F7FC",
    fontWeight: isActive ? 600 : 400,
    width: width,
    textTransform: "none",
    minWidth: "100px",
    fontSize: "0.875rem",
    padding: "6px 8px",
    flexShrink: 0,
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    // Force visibility in production
    visibility: "visible !important",
    opacity: "1 !important",
    position: "relative",
    zIndex: 1,
  };

  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={buttonStyles}
      // Force rendering with explicit props
      disabled={false}
      type="button"
    >
      {label}{count !== undefined ? ` (${count})` : ""}
    </Button>
  );
};

const HEAD_LABELS = {
  IPD: [
    { id: 1, label: "Request Date", value: "requestDate" },
    { id: 2, label: "UHID", value: "uhid" },
    { id: 3, label: "Name", value: "name" },
    { id: 4, label: "Requested By", value: "requestedBy" },
    { id: 5, label: "Ward Name", value: "wardName" },
    { id: 6, label: "Bed Number", value: "bedNumber" },
    { id: 7, label: "Expected Delivery", value: "expectedDelivery" },
    { id: 10, label: "Request/Return", value: "requestedType" },
    { id: 8, label: "Status", value: "status" },
    { id: 9, label: "", value: "", align: "right" },
  ],
  OPD: [
    { id: 1, label: "Date", value: "date" },
    { id: 2, label: "UHID", value: "uhid" },
    { id: 3, label: "Name", value: "name" },
    { id: 4, label: "Contact Number", value: "contactNumber" },
    { id: 5, label: "Requested By", value: "requestedBy" },
    { id: 6, label: "Invoice Number", value: "invoiceNumber" },
    { id: 7, label: "Status", value: "status" },
    { id: 8, label: "", value: "", align: "right" },
  ],
  OUTSIDER: [
    { id: 1, label: "Date", value: "date" },
    { id: 2, label: "UHID", value: "uhid" },
    { id: 3, label: "Name", value: "name" },
    { id: 4, label: "Contact Number", value: "contactNumber" },
    { id: 5, label: "Requested By", value: "requestedBy" },
    { id: 6, label: "Invoice Number", value: "invoiceNumber" },
    { id: 7, label: "Status", value: "status" },
    { id: 8, label: "", value: "", align: "right" },
  ],
};

const popoverOptions = ["View/Edit Details", "Approve Medicine Request", "Approve Medicine Return", "Bill"];

// Explicitly define buttons to prevent production build issues
const PHARMACY_STATUS_BUTTONS = [
  { label: "Pending", width: "110px", value: "Pending" },
  { label: "Approved", width: "110px", value: "Approved" },
  { label: "Rejected", width: "110px", value: "Rejected" },
  { label: "Returned", width: "110px", value: "Returned" },
  { label: "Delivered", width: "110px", value: "Delivered" },
];

const buttons = PHARMACY_STATUS_BUTTONS;

// Custom API function that flattens the nested response
const getFlattenedPharmacyRequestApi = async (params) => {
  const response = await getPharmacyRequestApi(params);
  if (response?.success || response?.isSuccess) {
    // Flatten the nested structure
    return {
      ...response,
      data: response.data?.data || response.data
    };
  }
  return response;
};

const Pharmacy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([]);
  const [visitType, setVisitType] = useState("IPD");
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [curPatientId, setCurPatientId] = useState("");
  const [activeButton, setActiveButton] = useState("Pending"); // Set default active button
  const [status, setStatus] = useState("Pending"); // Set default status
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("UHID");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
    setCurPatientId(row?.patientId);
  };

  // Wrapper that captures statusCounts from the response before useGetApi processes it
  const fetchPharmacyApiWithCounts = useCallback(async (params) => {
    const response = await getFlattenedPharmacyRequestApi(params);
    if (response?.success || response?.isSuccess) {
      const data = response?.data;
      if (data?.statusCounts) {
        setStatusCounts(data.statusCounts);
      }
    }
    return response;
  }, []);

  // Fetch all pharmacy requests for client-side sorting and pagination (like Consultation page)
  // dataKey and countKey are not working as expected that is why we need to change in this way
  const { dataList, dataCount, isLoading, isError, refetch } = useGetApi({
    apiFunction: fetchPharmacyApiWithCounts,
    params: { 
      limit: 10000, // Fetch large number for client-side pagination/sorting
      page: 1, 
      status, 
      search, 
      searchBy, 
      visitType,
      date: dateFilter,
      ...(dateFilter === "range" && { fromDate, toDate }),
      // Remove sort/order - will be handled client-side
    },
    dependencies: [status, search, searchBy, visitType, dateFilter, fromDate, toDate],
    // Remove paginationModel and sortModel from dependencies - handled client-side
    debounceDelay: 500,
  });

  // The useGetApi hook now returns the extracted data directly
  const pharmacyListData = dataList || [];
  const pharmacyListCount = dataCount || 0;


  const menuHandler = (index) => {
    handleClose();
        switch (index) {
      case 0:
        if (selectedRow?._id) {
          navigate(`/pharmacy/medicine-request/${selectedRow._id}`);
        } else {
          console.error("Tracking ID not found in selected row. Available fields:", Object.keys(selectedRow || {}));
          alert("Unable to view medicine request details. Request information not available.");
        }
        break;
      case 1:
        // Approve Medicine Request
        if (selectedRow?._id) {
          navigate(`/pharmacy/medicine-request/${selectedRow._id}`);
        } else {
          console.error("Tracking ID not found in selected row. Available fields:", Object.keys(selectedRow || {}));
          alert("Unable to approve medicine request. Request information not available.");
        }
        break;
      case 2:
        // Approve Medicine Return
        navigate(`/pharmacy/medicine-return/${selectedRow?._id}`);
        break;
      case 3:
        // Bill - Navigate to billing page
        
        // Try to get patientId from different possible sources
        const billingPatientId = selectedRow?.patientId || selectedRow?.patient?._id || selectedRow?.patient;
        
        if (billingPatientId) {
          navigate(`/billing/${billingPatientId}`);
        } else {
          console.error("Patient ID not found in selected row for billing. Available fields:", Object.keys(selectedRow || {}));
          // Show a user-friendly error message
          alert("Unable to access billing. Patient information not available.");
        }
        break;
      default:
        console.log("Unknown menu option:", index);
        break;
    }
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
    setCurPatientId(row?.patientId);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  // Handle outsider button click
  const handleOutsiderClick = () => {
    navigate('/pharmacy/outsider');
  };

  // Get headLabel based on visitType
  const headLabel = HEAD_LABELS[visitType] || [];

  // Prepare rows for DataGrid
  const rows = pharmacyListData?.map((row) => {
    return {
      ...row, // Spread raw data FIRST so explicit fields below can override
      id: row._id,
      _id: row._id,
      requestedDate: row.requestedDate,
      uhid: row.uhid || "-",
      patientName: row.patientName || "-",
      requestedBy: row.requestedBy?.firstName && row.requestedBy?.lastName
        ? `${row.requestedBy.firstName} ${row.requestedBy.lastName}`
        : typeof row.requestedBy === 'string' ? row.requestedBy : "-",
      wardName: row.wardName || "-",
      bedNumber: row.bedNumber || "-",
      expectedDelivery: row.expectedDelivery,
      requestType: row.requestType || "-",
      contactNumber: row.contactNumber || "-",
      invoiceNumber: row.invoiceNumber || "-",
      overallStatus: row.overallStatus || "-",
      patientId: row.patientId,
      patient: row.patient,
    };
  }) || [];

  // Responsive columns configuration
  const getColumns = useCallback(() => {
    const baseColumns = [];

    if (visitType === "IPD") {
      baseColumns.push(
        {
          field: 'requestedDate',
          headerName: 'Request Date',
          flex: isMobile ? 0 : 1,
          width: isMobile ? 100 : undefined,
          minWidth: 100,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
          valueFormatter: (params) => {
            return params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-";
          },
        },
        {
          field: 'uhid',
          headerName: 'UHID',
          flex: isMobile ? 0 : 0.8,
          width: isMobile ? 100 : undefined,
          minWidth: 90,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'patientName',
          headerName: 'Name',
          flex: isMobile ? 0 : 1.2,
          width: isMobile ? 120 : undefined,
          minWidth: 120,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'requestedBy',
          headerName: 'Requested By',
          flex: isTablet ? 0 : 1,
          width: isTablet ? 120 : undefined,
          minWidth: 120,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'wardName',
          headerName: 'Ward Name',
          flex: isTablet ? 0 : 0.8,
          width: isTablet ? 100 : undefined,
          minWidth: 100,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'bedNumber',
          headerName: 'Bed Number',
          flex: isTablet ? 0 : 0.7,
          width: isTablet ? 90 : undefined,
          minWidth: 90,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'expectedDelivery',
          headerName: 'Expected Delivery',
          flex: isTablet ? 0 : 1,
          width: isTablet ? 120 : undefined,
          minWidth: 120,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
          valueFormatter: (params) => {
            return params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-";
          },
        },
        {
          field: 'requestType',
          headerName: 'Request/Return',
          flex: isTablet ? 0 : 0.8,
          width: isTablet ? 100 : undefined,
          minWidth: 100,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'overallStatus',
          headerName: 'Status',
          flex: isTablet ? 0 : 0.8,
          width: isTablet ? 100 : undefined,
          minWidth: 90,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
          renderCell: (params) => (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                textAlign: "center",
                fontSize: "0.875rem",
                fontWeight: 500,
                backgroundColor: 
                  params.value === "Delivered" ? "#e8f5e8" :
                  params.value === "Pending" ? "#fff3e0" :
                  params.value === "Approved" ? "#e3f2fd" :
                  params.value === "Rejected" ? "#ffebee" :
                  params.value === "Returned" ? "#e8f5e8" : "#f5f5f5",
                color:
                  params.value === "Delivered" ? "#2e7d32" :
                  params.value === "Pending" ? "#f57c00" :
                  params.value === "Approved" ? "#1976d2" :
                  params.value === "Rejected" ? "#c62828" :
                  params.value === "Returned" ? "#2e7d32" : "#666",
              }}
            >
              {params.value || "-"}
            </Box>
          ),
        }
      );
    } else {
      // OPD and OUTSIDER columns
      baseColumns.push(
        {
          field: 'requestedDate',
          headerName: 'Date',
          flex: isMobile ? 0 : 0.8,
          width: isMobile ? 100 : undefined,
          minWidth: 100,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
          valueFormatter: (params) => {
            return params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-";
          },
        },
        {
          field: 'uhid',
          headerName: 'UHID',
          flex: isMobile ? 0 : 0.8,
          width: isMobile ? 100 : undefined,
          minWidth: 90,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'patientName',
          headerName: 'Name',
          flex: isMobile ? 0 : 1.2,
          width: isMobile ? 120 : undefined,
          minWidth: 120,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'contactNumber',
          headerName: 'Contact Number',
          flex: isMobile ? 0 : 1,
          width: isMobile ? 110 : undefined,
          minWidth: 110,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'requestedBy',
          headerName: 'Requested By',
          flex: isTablet ? 0 : 1,
          width: isTablet ? 120 : undefined,
          minWidth: 120,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'invoiceNumber',
          headerName: 'Invoice Number',
          flex: isTablet ? 0 : 1,
          width: isTablet ? 120 : undefined,
          minWidth: 120,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
        },
        {
          field: 'overallStatus',
          headerName: 'Status',
          flex: isTablet ? 0 : 0.8,
          width: isTablet ? 100 : undefined,
          minWidth: 90,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
          renderCell: (params) => (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                textAlign: "center",
                fontSize: "0.875rem",
                fontWeight: 500,
                backgroundColor: 
                  params.value === "Delivered" ? "#e8f5e8" :
                  params.value === "Pending" ? "#fff3e0" :
                  params.value === "Approved" ? "#e3f2fd" :
                  params.value === "Rejected" ? "#ffebee" :
                  params.value === "Returned" ? "#e8f5e8" : "#f5f5f5",
                color:
                  params.value === "Delivered" ? "#2e7d32" :
                  params.value === "Pending" ? "#f57c00" :
                  params.value === "Approved" ? "#1976d2" :
                  params.value === "Rejected" ? "#c62828" :
                  params.value === "Returned" ? "#2e7d32" : "#666",
              }}
            >
              {params.value || "-"}
            </Box>
          ),
        }
      );
    }

    // Add Actions column
    baseColumns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 0,
      width: 70,
      minWidth: 60,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="actions"
          size={isMobile ? "small" : "medium"}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e, params.row);
          }}
        >
          <MoreVert fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
      ),
    });

    // Responsive column hiding
    if (isMobile) {
      if (visitType === "IPD") {
        return baseColumns.filter(col => 
          ['requestedDate', 'uhid', 'patientName', 'actions'].includes(col.field)
        );
      } else {
        return baseColumns.filter(col => 
          ['requestedDate', 'uhid', 'patientName', 'actions'].includes(col.field)
        );
      }
    }
    if (isTablet) {
      if (visitType === "IPD") {
        return baseColumns.filter(col => 
          ['requestedDate', 'uhid', 'patientName', 'requestedBy', 'overallStatus', 'actions'].includes(col.field)
        );
      } else {
        return baseColumns.filter(col => 
          ['requestedDate', 'uhid', 'patientName', 'contactNumber', 'overallStatus', 'actions'].includes(col.field)
        );
      }
    }
    return baseColumns;
  }, [visitType, isMobile, isTablet]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              marginTop: "20px",
              marginBottom: "40px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                minWidth: "300px",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Dropdown - only show when search is focused */}
              {isSearchFocused && (
                <select
                  onChange={(e) => setSearchBy(e.target.value)}
                  value={searchBy}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    padding: "12px 16px",
                    fontSize: "14px",
                    width: `${Math.max(120, (searchBy === "UHID" ? "UHID" : 
                      searchBy === "patientName" ? "Name" : 
                      searchBy === "phone" ? "Phone" : "UHID").length * 9 + 30)}px`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRight: "1px solid #e0e0e0",
                    color: "#333",
                    fontWeight: "500",
                    touchAction: "manipulation"
                  }}
                >
                  <option value="UHID">UHID</option>
                  <option value="patientName">Name</option>
                  <option value="phone">Phone</option>
                </select>
              )}
              
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder={`Search by ${searchBy === "UHID" ? "UHID" : 
                    searchBy === "patientName" ? "Name" : 
                    searchBy === "phone" ? "Phone" : "UHID"}`}
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value || "")}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleOutsiderClick}
              sx={{
                textTransform: "none",
                minWidth: "150px"
              }}
            >
              + Add Outsider
            </Button>
          </Box>

          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "40px",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexShrink: 0,
              }}
            >
              <FormControl size="small" sx={{ minWidth: "120px" }}>
                <InputLabel id="visit-type-label">Visit Type</InputLabel>
                <Select
                  labelId="visit-type-label"
                  label="Visit Type"
                  value={visitType || ""}
                  onChange={(e) => setVisitType(e.target.value)}
                >
                  <MenuItem value="IPD">IPD</MenuItem>
                  <MenuItem value="OPD">OPD</MenuItem>
                  <MenuItem value="OUTSIDER">Outsider</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: "100px" }}>
                <InputLabel id="date-select-label">Date</InputLabel>
                <Select
                  labelId="date-select-label"
                  label="Date"
                  value={dateFilter || ""}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                  <MenuItem value="range">Range</MenuItem>
                </Select>
              </FormControl>

              {/* Show date range pickers if "Range" is selected */}
              {dateFilter === "range" && (
                <>
                  <DatePicker
                    label="From Date"
                    value={fromDate ? dayjs(fromDate, "YYYY-MM-DD") : null}
                    onChange={(newValue) =>
                      setFromDate(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                      )
                    }
                    slotProps={{ textField: { size: "small" } }}
                    sx={{ width: "150px" }}
                  />
                  <DatePicker
                    label="To Date"
                    value={toDate ? dayjs(toDate, "YYYY-MM-DD") : null}
                    onChange={(newValue) =>
                      setToDate(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                      )
                    }
                    slotProps={{ textField: { size: "small" } }}
                    sx={{ width: "150px" }}
                  />
                </>
              )}
            </Box>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              minHeight: "40px",
              overflow: "visible",
              visibility: "visible !important",
              opacity: "1 !important",
              position: "relative",
              zIndex: 1,
            }}>
                            {/* Render pharmacy status buttons using dedicated component to ensure visibility in production */}
              {/* Force render all buttons with explicit error handling */}
              {(() => {
                try {
                  return (
                    <>
                      <PharmacyStatusButton
                        label="Pending"
                        isActive={activeButton === "Pending"}
                        onClick={() => {
                          setActiveButton("Pending");
                          setStatus("Pending");
                        }}
                        width="130px"
                        count={statusCounts["Pending"] ?? 0}
                      />
                      <PharmacyStatusButton
                        label="Approved"
                        isActive={activeButton === "Approved"}
                        onClick={() => {
                          setActiveButton("Approved");
                          setStatus("Approved");
                        }}
                        width="130px"
                        count={statusCounts["Approved"] ?? 0}
                      />
                      <PharmacyStatusButton
                        label="Rejected"
                        isActive={activeButton === "Rejected"}
                        onClick={() => {
                          setActiveButton("Rejected");
                          setStatus("Rejected");
                        }}
                        width="130px"
                        count={statusCounts["Rejected"] ?? 0}
                      />
                      <PharmacyStatusButton
                        label="Returned"
                        isActive={activeButton === "Returned"}
                        onClick={() => {
                          setActiveButton("Returned");
                          setStatus("Returned");
                        }}
                        width="130px"
                        count={statusCounts["Returned"] ?? 0}
                      />
                      <PharmacyStatusButton
                        label="Delivered"
                        isActive={activeButton === "Delivered"}
                        onClick={() => {
                          setActiveButton("Delivered");
                          setStatus("Delivered");
                        }}
                        width="130px"
                        count={statusCounts["Delivered"] ?? 0}
                      />
                    </>
                  );
                } catch (error) {
                  // Fallback rendering if component fails
                  console.error("PharmacyStatusButton rendering failed:", error);
                  return (
                    <>
                      <Button variant="outlined" sx={{ width: "110px", textTransform: "none", visibility: "visible !important" }}>Pending</Button>
                      <Button variant="outlined" sx={{ width: "110px", textTransform: "none", visibility: "visible !important" }}>Approved</Button>
                      <Button variant="outlined" sx={{ width: "110px", textTransform: "none", visibility: "visible !important" }}>Rejected</Button>
                      <Button variant="outlined" sx={{ width: "110px", textTransform: "none", visibility: "visible !important" }}>Returned</Button>
                      <Button variant="outlined" sx={{ width: "110px", textTransform: "none", visibility: "visible !important" }}>Delivered</Button>
                    </>
                  );
                }
              })()}
            </Box>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box sx={{ 
              height: { 
                xs: 'calc(100vh - 300px)', 
                sm: 'calc(100vh - 320px)', 
                md: 'calc(100vh - 350px)' 
              },
              width: '100%',
              minHeight: '300px',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="client"
                sortingMode="client"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                onCellClick={(params, event) => {
                  if (params.field !== 'actions') {
                    handleClick(event, params.row);
                  }
                }}
                onRowContextMenu={(params, event) => {
                  event.preventDefault();
                  handleRightClick(event, params.row);
                }}
                componentsProps={{
                  row: {
                    onContextMenu: (event) => {
                      event.preventDefault();
                    },
                  },
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    width: '100%',
                  },
                  '& .MuiDataGrid-main': {
                    width: '100%',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                    padding: isMobile ? '4px' : '8px',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      cursor: 'pointer',
                    },
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    '& .MuiDataGrid-columnHeader': {
                      fontSize: '11px',
                      padding: '4px',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: '11px',
                      padding: '2px 4px',
                    },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        No pharmacy requests or returns found for the selected criteria.
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}

          {/* Pop-up Menu */}
          <Menu
            id={Boolean(anchorEl) ? "row-popover" : undefined}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
            anchorPosition={
              anchorPosition
                ? { top: anchorPosition.top, left: anchorPosition.left }
                : undefined
            }
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "8px",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  maxHeight: "300px",
                },
              },
            }}
          >
            {popoverOptions?.map((option, index) => (
              <MenuItem 
                key={index}
                onClick={() => menuHandler(index)}
                sx={{
                  "&:hover": {
                    backgroundColor: "#f5f5f5"
                  }
                }}
              >
                {option || ""}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
    </Container>
  );
};

export default Pharmacy;
